// ═══════════════════════════════════════════════════════════════════════════════
//  AssessmentResult — LA RADIOGRAFÍA
//
//  Esta pantalla es la razón de ser del examen. Un número ("B1") no sirve para
//  entrenar a nadie; lo que sirve es el desglose por skill con lo que domina y
//  lo que se le rompe. Por eso las competencias se muestran con el mismo peso
//  visual que el nivel — no como letra pequeña.
//
//  Incluye dos estados: desbloqueado (plan de pago) y bloqueado (plan gratuito).
// ═══════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { Link } from 'react-router-dom';

const LEVELS = ['pre-A1', 'A1', 'A2', 'B1', 'B2', 'C1'];

const SKILL_ICON = {
  use_of_english: '🧩',
  reading: '📖',
  listening: '🎧',
  speaking: '🗣️',
  writing: '✍️',
};

/** Traduce una etiqueta técnica a algo que el alumno entienda. */
const TAG_ES = {
  verb_to_be: 'Verbo "to be"',
  word_order_svo: 'Orden de la frase',
  third_person_s: 'Tercera persona (-s)',
  articles: 'Artículos a / an / the',
  past_simple_irregular: 'Pasado simple irregular',
  present_continuous: 'Presente continuo',
  comparatives: 'Comparativos',
  countable_uncountable: 'Contables e incontables',
  present_perfect: 'Present perfect',
  conditional_2: 'Condicional tipo 2',
  present_perfect_continuous: 'Present perfect continuous',
  modals_deduction: 'Modales de deducción',
  concessive_connectors: 'Conectores concesivos',
  future_perfect: 'Futuro perfecto',
  passive_voice: 'Voz pasiva',
  object_pronouns: 'Pronombres objeto',
  phrasal_verbs: 'Phrasal verbs',
  inversion: 'Inversión enfática',
  perfect_participle: 'Participio compuesto',
  formal_register: 'Registro formal',
  literal_comprehension: 'Comprensión literal',
  numbers: 'Números',
  vocabulary_in_context: 'Vocabulario en contexto',
  main_idea: 'Idea principal',
  detail_extraction: 'Localizar detalles',
  inference: 'Inferencia',
  cohesion: 'Cohesión del texto',
  author_tone: 'Tono del autor',
  subtext: 'Subtexto',
  argument_structure: 'Estructura argumental',
  daily_routines: 'Rutinas diarias',
  basic_vocabulary: 'Vocabulario básico',
  tone_basic: 'Tono básico',
  time_expressions: 'Expresiones de tiempo',
  gist: 'Idea general al escuchar',
  detail_audio: 'Detalle en audio',
  tone_conversational: 'Tono conversacional',
  tone_sarcasm: 'Sarcasmo',
  inference_audio: 'Inferencia al escuchar',
  conditional_3_inversion: 'Condicional 3 con inversión',
  tone_indifference: 'Tono de indiferencia',
  hedging: 'Matizar opiniones',
  business_context: 'Contexto de negocios',
  diplomatic_language: 'Lenguaje diplomático',
  passive_aggressive: 'Cortesía pasivo-agresiva',
  idiomatic_advanced: 'Expresiones idiomáticas',
  // ── Competencias de producción (Writing / Speaking) ──
  task_achievement: 'Cumplir la tarea',
  cohesion_devices: 'Conectores',
  vocabulary_range: 'Riqueza de vocabulario',
  grammar_range: 'Variedad de estructuras',
  grammar_accuracy: 'Corrección gramatical',
  word_order: 'Orden de palabras',
  prepositions: 'Preposiciones',
  register: 'Registro (formal/informal)',
  spelling: 'Ortografía',
  fluency: 'Fluidez',
  response_length: 'Extensión de la respuesta',
};

// ── LA PANTALLA EN BLANCO DEL 20-09-2026 ────────────────────────────────────
// Rob: «/evaluacion no está conectando, se ve pantalla blanca». Esta función
// recibía una etiqueta `undefined` —de un resultado guardado antes de que
// existieran las etiquetas— y `undefined.replace` tumbaba la página entera.
//
// Una etiqueta que no se entiende se calla; nunca vale una pantalla vacía.
const label = (tag) => {
  if (typeof tag !== 'string' || !tag.trim()) return null;
  return TAG_ES[tag] || tag.replace(/_/g, ' ');
};

/** Las etiquetas de un resultado, sin huecos ni duplicados. */
const etiquetas = (lista) => (Array.isArray(lista) ? lista : [])
  .filter((x) => typeof x === 'string' && x.trim());

function levelPct(level) {
  const i = LEVELS.indexOf(level);
  return i < 0 ? 0 : (i / (LEVELS.length - 1)) * 100;
}

/* ─────────────────────────────────────────────────────────────────────────── */

const CRITERIA_ES = {
  task_achievement: 'Logró la tarea',
  coherence: 'Coherencia',
  range: 'Riqueza',
  accuracy: 'Corrección',
};

/** Métricas objetivas de una tarea productiva: se calculan sin IA. */
function Metrics({ m }) {
  if (!m || !m.word_count) return null;
  return (
    <div className="as-metrics">
      <div className="as-metric">
        <span className="as-metric-val">{m.word_count}</span>
        <span className="as-metric-key">palabras</span>
      </div>
      {m.min_words ? (
        <div className="as-metric">
          <span className="as-metric-val">{m.meets_minimum ? '✓' : '✕'}</span>
          <span className="as-metric-key">mín. {m.min_words}</span>
        </div>
      ) : null}
      {m.duration_seconds ? (
        <div className="as-metric">
          <span className="as-metric-val">{Math.round(m.duration_seconds)}s</span>
          <span className="as-metric-key">duración</span>
        </div>
      ) : null}
      {m.words_per_minute ? (
        <div className="as-metric">
          <span className="as-metric-val">{Math.round(m.words_per_minute)}</span>
          <span className="as-metric-key">palabras/min</span>
        </div>
      ) : null}
    </div>
  );
}

function Criteria({ c }) {
  const rows = Object.entries(c || {}).filter(([, v]) => typeof v === 'number');
  if (!rows.length) return null;
  return (
    <div className="as-criteria">
      {rows.map(([k, v]) => (
        <div key={k} className="as-crit">
          <span className="as-crit-name">{CRITERIA_ES[k] || k}</span>
          <span className="as-crit-bar">
            <span className="as-crit-fill" style={{ width: `${(v / 5) * 100}%` }} />
          </span>
          <span className="as-crit-val">{v}/5</span>
        </div>
      ))}
    </div>
  );
}

function SkillCard({ s }) {
  const lowConfidence = (s.confidence ?? 1) < 0.5;
  const pending = s.status === 'pending_evaluation';

  return (
    <article className="as-rcard">
      <header className="as-rcard-head">
        <span className="as-rcard-icon" aria-hidden="true">{SKILL_ICON[s.skill] || '•'}</span>
        <div>
          <h3 className="as-rcard-name">{s.label}</h3>
          <p className="as-rcard-meta">
            {s.productive
              ? `${s.items_answered ?? 0} ${s.items_answered === 1 ? 'tarea' : 'tareas'}`
              : `${s.items_answered ?? s.respondidas ?? 0} preguntas · ${s.score ?? 0}% de aciertos`}
          </p>
        </div>
        <span className="as-rcard-level">{s.cefr_level || '—'}</span>
      </header>

      {s.productive && <Metrics m={s.metrics} />}

      {pending && (
        <p className="as-pending">
          Tu respuesta quedó guardada y se está evaluando. Vuelve en un momento
          para ver el detalle.
        </p>
      )}

      {s.productive && !pending && <Criteria c={s.criteria} />}

      {s.productive && s.feedback && <p className="as-feedback">{s.feedback}</p>}

      {s.cefr_level && (
        <div className="as-ladder" role="img" aria-label={`Nivel ${s.cefr_level}`}>
          <div className="as-ladder-fill" style={{ width: `${levelPct(s.cefr_level)}%` }} />
          {LEVELS.slice(1).map((l) => (
            <span key={l} className="as-ladder-tick">{l}</span>
          ))}
        </div>
      )}

      {lowConfidence && !pending && s.cefr_level && (
        <p className="as-lowconf">
          {s.productive
            ? 'Medición preliminar: una sola tarea corta no basta para fijar un nivel de producción. Se afina con cada sesión que entrenes.'
            : 'Medición preliminar: respondiste pocas preguntas de esta habilidad, así que el nivel puede moverse cuando entrenes.'}
        </p>
      )}

      {/* `mastered_tags` y `weak_tags` pueden no venir: los resultados
          guardados antes de que existiera el diagnóstico no los tienen. */}
      <div className="as-tags">
        <div>
          <span className="as-tag-label as-tag-ok">Dominas</span>
          {etiquetas(s.mastered_tags).length ? (
            <ul className="as-taglist">
              {etiquetas(s.mastered_tags).map((t) => (
                <li key={t} className="as-tag as-tag-good">{label(t)}</li>
              ))}
            </ul>
          ) : (
            <p className="as-tag-none">Todavía no hay nada consolidado en este nivel.</p>
          )}
        </div>

        <div>
          <span className="as-tag-label as-tag-weak">Se te rompe</span>
          {etiquetas(s.weak_tags).length ? (
            <ul className="as-taglist">
              {etiquetas(s.weak_tags).map((t) => (
                <li key={t} className="as-tag as-tag-bad">{label(t)}</li>
              ))}
            </ul>
          ) : (
            <p className="as-tag-none">Sin puntos débiles claros en lo que respondiste.</p>
          )}
        </div>
      </div>
    </article>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */

export default function AssessmentResult({ result, onTrain, onRepetir }) {
  // La confirmación de «volver a medirte». Vive aquí y no en la página porque
  // es una decisión de ESTA pantalla: la página sólo recibe el «sí».
  const [confirmando, setConfirmando] = useState(false);

  /* ── Estado bloqueado: plan gratuito ─────────────────────────────────────── */
  if (result.locked) {
    return (
      <div className="as-screen">
        <p className="as-kicker">Evaluación terminada</p>
        <h1 className="as-title">Tu radiografía está lista</h1>

        <div className="as-overall as-overall-locked">
          <span className="as-overall-label">Nivel general estimado</span>
          <span className="as-overall-level">{result.overall_cefr}</span>
        </div>

        <div className="as-locked">
          <div className="as-locked-rows" aria-hidden="true">
            {(result.skills_preview || []).map((s) => (
              <div key={s.skill} className="as-locked-row">
                <span className="as-rcard-icon">{SKILL_ICON[s.skill] || '•'}</span>
                <span className="as-locked-name">{s.label}</span>
                <span className="as-locked-blur" />
              </div>
            ))}
          </div>
          <div className="as-locked-veil">
            <span className="as-lock-icon" aria-hidden="true">🔒</span>
            <p className="as-locked-msg">
              El desglose por habilidad y el diagnóstico de qué dominas y qué se
              te rompe se desbloquean con cualquier plan de pago.
            </p>
          </div>
        </div>

        <p className="as-honesty">
          <strong>Tus respuestas quedan guardadas.</strong> No vas a tener que
          repetir el examen: el día que cambies de plan, esta misma radiografía
          se abre tal cual.
        </p>

        <div className="as-actions">
          <Link to="/plans" className="as-btn as-btn-lg">Ver planes →</Link>
          <Link to="/entrenar" className="as-link as-center">Volver al gimnasio</Link>
        </div>
      </div>
    );
  }

  /* ── Estado desbloqueado ─────────────────────────────────────────────────── */
  const skills = result.skills || [];
  const allWeak = [...new Set(skills.flatMap((s) => etiquetas(s.weak_tags)))];
  const minutes = Math.max(1, Math.round((result.total_time_seconds || 0) / 60));

  return (
    <div className="as-screen">
      <p className="as-kicker">Tu radiografía</p>
      <h1 className="as-title">Esto es lo que mide tu inglés hoy</h1>

      <div className="as-overall">
        <span className="as-overall-label">Nivel general</span>
        <span className="as-overall-level">{result.overall_cefr}</span>
        <span className="as-overall-meta">
          {result.overall_score}% de aciertos · {minutes} min
        </span>
      </div>

      <p className="as-lead">
        Tu nivel no es el mismo en todas las habilidades — casi nunca lo es. Por
        eso lo que sigue importa más que el promedio de arriba.
      </p>

      <div className="as-rgrid">
        {skills.map((s) => <SkillCard key={s.skill} s={s} />)}
      </div>

      {allWeak.length > 0 && (
        <div className="as-focus">
          <h2 className="as-focus-title">Por aquí empieza tu entrenamiento</h2>
          <p className="as-focus-text">
            Estas son las competencias donde fallaste de forma consistente. No
            son un regaño: son la lista exacta de lo que hay que trabajar.
          </p>
          <ul className="as-taglist">
            {allWeak.map((t) => <li key={t} className="as-tag as-tag-bad">{label(t)}</li>)}
          </ul>
        </div>
      )}

      <p className="as-honesty">
        Esta es tu foto de hoy, no tu límite. Cada sesión real que entrenes da
        información más precisa que un examen — y tu nivel se vuelve a medir con
        lo que hagas, no con lo que contestaste una tarde.
      </p>

      <div className="as-actions">
        <button type="button" className="as-btn as-btn-lg" onClick={onTrain}>
          Empezar a entrenar →
        </button>

        {/* ── VOLVER A MEDIRSE ─────────────────────────────────────────────
            Rob: «¿qué pasaría si un alumno se quiere volver a poner a prueba?
            No tenemos un botón de volver a hacer, con su modal de "estás
            seguro"».

            No lo había, y el backend ya lo permitía: `POST /start` abre una
            sesión nueva cuando la anterior está terminada. Faltaba la puerta.

            Va DEBAJO y en discreto a propósito: lo que el alumno tiene que
            hacer después de medirse es entrenar, no volver a medirse. Y con
            confirmación, porque el examen son unos minutos y no se empieza
            sin querer. */}
        {onRepetir && (
          <button type="button" className="as-link as-center"
                  data-prueba="evaluacion-repetir"
                  onClick={() => setConfirmando(true)}>
            Volver a hacer la prueba
          </button>
        )}
      </div>

      {confirmando && (
        <div className="as-sobre" role="dialog" aria-modal="true"
             aria-labelledby="as-confirmar-titulo" data-prueba="evaluacion-confirmar">
          <div className="as-dialogo">
            <h2 className="as-dialogo-titulo" id="as-confirmar-titulo">
              ¿Volver a medirte?
            </h2>
            {/* Lo que pasa de verdad, sin adornos: el examen son unos minutos
                y el nivel que se enseña a partir de ahora es el nuevo. Lo que
                NO pasa también se dice, porque es lo que da tranquilidad:
                nada de lo conseguido se borra. */}
            <p className="as-dialogo-texto">
              Empiezas de cero y son unos minutos. Al terminar, el nivel que
              verás en tu perfil será el nuevo.
            </p>
            <p className="as-dialogo-texto">
              Tu resultado de hoy se queda guardado, y tu progreso, tus rachas
              y tu vocabulario no se tocan.
            </p>
            <div className="as-dialogo-botones">
              <button type="button" className="as-btn"
                      data-prueba="evaluacion-repetir-si"
                      onClick={() => { setConfirmando(false); onRepetir(); }}>
                Sí, volver a medirme
              </button>
              <button type="button" className="as-link"
                      data-prueba="evaluacion-repetir-no"
                      onClick={() => setConfirmando(false)}>
                Mejor no
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
